import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    InputAdornment,
    MenuItem,
    Modal,
    IconButton,
    Paper,
    Select,
    Snackbar,
    TextField,
    Typography,
    Chip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getHouseEntity, updateHouse } from '../../api_calls/api_calls_houses';
import { useTheme } from '@mui/material/styles';

const floorOptions = ['Basement', 'Ground floor', '1', '2', '3', '4', '5', 'Roof'];

const EditHouseModal = ({ open, onClose, houseId, apartmentName, apartmentAddress, dictionary, onUpdated }) => {
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const [saving, setSaving] = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plans, setPlans] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPlans, setFilteredPlans] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [values, setValues] = useState({
        name: '',
        address: '',
        area: '',
        year: '',
        floor: '',
        invoiceprovidername: '',
        invoicename: '',
        typehouse: '',
    });
    const [billingMetadata, setBillingMetadata] = useState({
        dteuroanatm: '',
        dfeuroanatm: '',
        timizonis: '',
        palaiotita: '',
        synttap: '',
        nightmeter: false,
        simfisxyspar: '',
    });

    useEffect(() => {
        if (!open || !houseId) return;
        let mounted = true;
        setLoading(true);
        setPlansLoading(true);
        setError('');

        Promise.allSettled([getHouseEntity(houseId), axios.get('/invoice/getall')])
            .then(([houseRes, plansRes]) => {
                if (!mounted) return;
                if (houseRes.status === 'fulfilled') {
                    const house = houseRes.value || {};
                    setValues({
                        name: house?.name || '',
                        address: house?.address || '',
                        area: house?.area != null ? String(house.area) : '',
                        year: house?.year != null ? String(house.year) : '',
                        floor: house?.floor != null ? String(house.floor) : '',
                        invoiceprovidername: house?.invoiceprovidername || '',
                        invoicename: house?.invoicename || '',
                        typehouse: house?.typehouse || '',
                    });
                    setBillingMetadata({
                        dteuroanatm: house?.dteuroanatm != null ? String(house.dteuroanatm) : '',
                        dfeuroanatm: house?.dfeuroanatm != null ? String(house.dfeuroanatm) : '',
                        timizonis: house?.timizonis != null ? String(house.timizonis) : '',
                        palaiotita: house?.palaiotita != null ? String(house.palaiotita) : '',
                        synttap: house?.synttap != null ? String(house.synttap) : '',
                        nightmeter: Boolean(house?.nightmeter),
                        simfisxyspar: house?.simfisxyspar != null ? String(house.simfisxyspar) : '',
                    });
                } else {
                    setError(dictionary.editHouseLoadError || 'Failed to load house');
                }

                if (plansRes.status === 'fulfilled') {
                    const list = Array.isArray(plansRes.value?.data) ? plansRes.value.data : [];
                    setPlans(list);
                    setFilteredPlans(list);
                }
            })
            .catch(() => {
                if (!mounted) return;
                setError(dictionary.editHouseLoadError || 'Failed to load house');
            })
            .finally(() => {
                if (!mounted) return;
                setLoading(false);
                setPlansLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [open, houseId, dictionary.editHouseLoadError]);

    const selectedPlan = useMemo(
        () => plans.find((p) => p?.provider_name === values.invoiceprovidername && p?.name === values.invoicename) || null,
        [plans, values.invoiceprovidername, values.invoicename],
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const handlePlanSelect = (plan) => {
        setValues((prev) => ({
            ...prev,
            invoiceprovidername: plan?.provider_name || '',
            invoicename: plan?.name || '',
        }));
    };
    const handleBillingMetadataChange = (e) => {
        const { name, value } = e.target;
        setBillingMetadata((prev) => ({ ...prev, [name]: value }));
    };
    const handleBillingBooleanChange = (e) => {
        const { name, value } = e.target;
        setBillingMetadata((prev) => ({ ...prev, [name]: value === true || value === 'true' }));
    };

    useEffect(() => {
        let filtered = plans;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = plans.filter((plan) => {
                const displayName = (plan?.display_name || '').toLowerCase();
                const providerName = (plan?.provider_name || '').toLowerCase();
                const description = (plan?.description || '').toLowerCase();
                return displayName.includes(query) || providerName.includes(query) || description.includes(query);
            });
        }
        const currentPlanIndex = filtered.findIndex(
            (plan) => plan?.provider_name === values.invoiceprovidername && plan?.name === values.invoicename,
        );
        if (currentPlanIndex > 0) {
            const currentPlan = filtered[currentPlanIndex];
            filtered = [currentPlan, ...filtered.filter((_, i) => i !== currentPlanIndex)];
        }
        setFilteredPlans(filtered);
    }, [searchQuery, plans, values.invoiceprovidername, values.invoicename]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!values.name?.trim() || !values.address?.trim()) {
            setError(dictionary.editHouseValidationError || 'Name and address are required');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: values.name.trim(),
                address: values.address.trim(),
                area: values.area !== '' ? Number(values.area) : 0,
                year: values.year !== '' ? Number(values.year) : 0,
                floor: values.floor || '',
                invoiceprovidername: values.invoiceprovidername || '',
                invoicename: values.invoicename || '',
                typehouse: values.typehouse || '',
                dteuroanatm: billingMetadata.dteuroanatm !== '' ? Number(billingMetadata.dteuroanatm) : 0,
                dfeuroanatm: billingMetadata.dfeuroanatm !== '' ? Number(billingMetadata.dfeuroanatm) : 0,
                timizonis: billingMetadata.timizonis !== '' ? Number(billingMetadata.timizonis) : 0,
                palaiotita: billingMetadata.palaiotita !== '' ? Number(billingMetadata.palaiotita) : 0,
                synttap: billingMetadata.synttap !== '' ? Number(billingMetadata.synttap) : 0,
                nightmeter: Boolean(billingMetadata.nightmeter),
                simfisxyspar: billingMetadata.simfisxyspar !== '' ? Number(billingMetadata.simfisxyspar) : 0,
            };
            await updateHouse(houseId, payload);
            setSuccess(true);
            if (typeof onUpdated === 'function') onUpdated();
            onClose();
        } catch (_) {
            setError(dictionary.editHouseSaveError || 'Failed to save house');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Paper
                    elevation={8}
                    sx={{ width: '100%', maxWidth: 760, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <Box sx={{ px: 2.5, py: 2, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                <Avatar sx={{ width: 38, height: 38, bgcolor: theme.palette.primary.main }}>
                                    <HomeIcon sx={{ fontSize: 22, color: '#fff' }} />
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                                        {dictionary.editHouseTitle || 'Edit House'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                        {dictionary.editHouseSubtitle || 'Update house information and billing details'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto', justifyContent: 'flex-end' }}>
                                <Chip
                                    icon={<HomeIcon color="primary" sx={{ fontSize: 20 }} />}
                                    label={apartmentName || values.name || '-'}
                                    size="medium"
                                    sx={{
                                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                                        color: 'primary.main',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'primary.light',
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                                    label={apartmentAddress || values.address || '-'}
                                    variant="outlined"
                                    sx={{ borderRadius: 2, borderColor: 'primary.light' }}
                                />
                                <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth required name="name" label={dictionary.editHouseName || 'House name'} value={values.name} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth required name="address" label={dictionary.address} value={values.address} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth name="area" type="number" label={dictionary.editHouseArea || 'Square meters'} value={values.area} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth name="year" type="number" label={dictionary.editHouseYear || 'Year'} value={values.year} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>{dictionary.floor}</InputLabel>
                                        <Select name="floor" value={values.floor} label={dictionary.floor} onChange={handleChange}>
                                            {floorOptions.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                        {dictionary.editHousePlan || 'Electricity Plan'}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder={dictionary.editHousePlanSearch || 'Search plans...'}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ maxHeight: 280, overflowY: 'auto', p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                        {plansLoading ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                                {dictionary.editHouseLoadingPlans || 'Loading plans...'}
                                            </Typography>
                                        ) : filteredPlans.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                                {searchQuery
                                                    ? (dictionary.editHouseNoPlansSearch || 'No plans found matching your search.')
                                                    : (dictionary.editHouseNoPlan || 'No plan')}
                                            </Typography>
                                        ) : (
                                            filteredPlans.map((plan, index) => {
                                                const isSelected =
                                                    selectedPlan?.provider_name === plan?.provider_name &&
                                                    selectedPlan?.name === plan?.name;
                                                const label = plan?.display_name || `${plan?.provider_name || ''} - ${plan?.name || ''}`;
                                                return (
                                                    <Box
                                                        key={`${plan?.provider_name || 'provider'}-${plan?.name || 'name'}-${index}`}
                                                        onClick={() => handlePlanSelect(plan)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            p: 1.25,
                                                            mb: 1,
                                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.10)' : '#fff',
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: isSelected ? 'primary.main' : '#e5e7eb',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                bgcolor: '#f8fafc',
                                                                borderColor: 'primary.main',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 4px 8px rgba(148,163,184,0.25)',
                                                            },
                                                        }}
                                                    >
                                                        <Avatar
                                                            src={`/images/providers/${plan?.provider_name || ''}.png`}
                                                            alt={plan?.provider_name || 'provider'}
                                                            sx={{ width: 42, height: 42, bgcolor: '#eef2ff', border: '1px solid #cbd5e1' }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                                                {label}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(plan?.provider_name || '').replace(/_/g, ' ')}
                                                            </Typography>
                                                        </Box>
                                                        <ArrowForwardIosIcon sx={{ color: '#94a3b8', fontSize: 16 }} />
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </Box>
                                    {selectedPlan && (
                                        <Alert severity="info" sx={{ mt: 1.5 }}>
                                            <Typography variant="body2">
                                                <strong>{dictionary.editHouseSelectedPlan || 'Selected Plan'}:</strong>{' '}
                                                {selectedPlan.display_name || selectedPlan.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ mt: 0.4, display: 'block' }}>
                                                {dictionary.editHousePlanWarning || 'Changing your electricity plan may affect billing calculations.'}
                                            </Typography>
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 0.5 }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {dictionary.editHouseAdditionalInfo || 'Additional House Info'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="dteuroanatm"
                                        label={dictionary.editHouseMunicipalFees || 'Municipal fees per m²'}
                                        type="number"
                                        value={billingMetadata.dteuroanatm}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="dfeuroanatm"
                                        label={dictionary.editHouseMunicipalTaxes || 'Municipal taxes per m²'}
                                        type="number"
                                        value={billingMetadata.dfeuroanatm}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="timizonis"
                                        label={dictionary.editHouseZonePrice || 'Zone Price'}
                                        type="number"
                                        value={billingMetadata.timizonis}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="palaiotita"
                                        label={dictionary.editHouseBuildingAge || 'Building Age'}
                                        type="number"
                                        value={billingMetadata.palaiotita}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="synttap"
                                        label={dictionary.editHouseTapCoefficient || 'TAP Coefficient'}
                                        type="number"
                                        value={billingMetadata.synttap}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="simfisxyspar"
                                        label={dictionary.editHouseSupplyPower || 'Agreed Supply Power (kW)'}
                                        type="number"
                                        value={billingMetadata.simfisxyspar}
                                        onChange={handleBillingMetadataChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>{dictionary.editHouseNightTariff || 'Night Tariff'}</InputLabel>
                                        <Select
                                            name="nightmeter"
                                            value={billingMetadata.nightmeter}
                                            label={dictionary.editHouseNightTariff || 'Night Tariff'}
                                            onChange={handleBillingBooleanChange}
                                        >
                                            <MenuItem value={false}>{dictionary.editHouseNo || 'No'}</MenuItem>
                                            <MenuItem value={true}>{dictionary.editHouseYes || 'Yes'}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={onClose} disabled={saving}>
                                {dictionary.editHouseCancel || 'Cancel'}
                            </Button>
                            <Button type="submit" variant="contained" disabled={loading || saving}>
                                {saving ? <CircularProgress size={20} color="inherit" /> : dictionary.editHouseSave || 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Modal>

            <Snackbar open={success} autoHideDuration={2500} onClose={() => setSuccess(false)}>
                <Alert severity="success" variant="filled" onClose={() => setSuccess(false)}>
                    {dictionary.editHouseSuccess || 'House updated successfully'}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditHouseModal;
